import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Kbd } from '@/components/ui/kbd';

interface KeyboardShortcutsHelpProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface ShortcutGroup {
  title: string;
  shortcuts: { keys: string[]; description: string }[];
}

const shortcutGroups: ShortcutGroup[] = [
  {
    title: 'Tools',
    shortcuts: [
      { keys: ['V'], description: 'Select tool' },
      { keys: ['H'], description: 'Hand (pan) tool' },
      { keys: ['S'], description: 'Sticky note' },
      { keys: ['T'], description: 'Text card' },
      { keys: ['D'], description: 'Document' },
      { keys: ['B'], description: 'Board' },
      { keys: ['L'], description: 'Link' },
    ],
  },
  {
    title: 'Navigation',
    shortcuts: [
      { keys: ['Space', 'Drag'], description: 'Temporary pan' },
      { keys: ['↑', '↓', '←', '→'], description: 'Pan canvas' },
      { keys: ['Shift', '↑↓←→'], description: 'Fast pan' },
      { keys: ['Scroll'], description: 'Pan vertically' },
      { keys: ['⌘/Ctrl', 'Scroll'], description: 'Zoom' },
    ],
  },
  {
    title: 'Zoom',
    shortcuts: [
      { keys: ['+', '='], description: 'Zoom in' },
      { keys: ['-'], description: 'Zoom out' },
      { keys: ['0'], description: 'Reset to 100%' },
      { keys: ['1'], description: 'Fit all items' },
    ],
  },
  {
    title: 'Selection',
    shortcuts: [
      { keys: ['⌘/Ctrl', 'A'], description: 'Select all' },
      { keys: ['Esc'], description: 'Deselect all' },
      { keys: ['Delete'], description: 'Delete selected' },
      { keys: ['Shift', 'Click'], description: 'Add to selection' },
    ],
  },
  {
    title: 'Edit',
    shortcuts: [
      { keys: ['⌘/Ctrl', 'Z'], description: 'Undo' },
      { keys: ['⌘/Ctrl', 'Shift', 'Z'], description: 'Redo' },
      { keys: ['⌘/Ctrl', 'V'], description: 'Paste' },
    ],
  },
  {
    title: 'View',
    shortcuts: [
      { keys: ['G'], description: 'Toggle grid' },
      { keys: ['?'], description: 'Show shortcuts' },
    ],
  },
];

export function KeyboardShortcutsHelp({ open, onOpenChange }: KeyboardShortcutsHelpProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">Keyboard Shortcuts</DialogTitle>
        </DialogHeader>
        
        <div className="grid grid-cols-2 gap-6 mt-4">
          {shortcutGroups.map((group) => (
            <div key={group.title} className="space-y-2">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                {group.title}
              </h3>
              <div className="space-y-1.5">
                {group.shortcuts.map((shortcut, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between py-1"
                  >
                    <span className="text-sm text-foreground">
                      {shortcut.description}
                    </span>
                    <div className="flex items-center gap-1">
                      {shortcut.keys.map((key, keyIndex) => (
                        <React.Fragment key={keyIndex}>
                          {keyIndex > 0 && (
                            <span className="text-xs text-muted-foreground">+</span>
                          )}
                          <Kbd>{key}</Kbd>
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        
        <div className="mt-6 pt-4 border-t text-center">
          <p className="text-sm text-muted-foreground">
            Press <Kbd>?</Kbd> anytime to show this help
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
